import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { initializeApp } from "firebase/app";
import { updateFcmToken } from "./api";
import { toast } from 'react-toastify';

// Flusso semplificato:
// Pre Login: se ho dato consenso alle notifiche e quindi ce le ho attive e posso visualizzarle, refresho il token fcm
// e lo invio al server insieme al login
// Post-login: showNotificationPrompt() se notifiche non attive quindi chiedo di attivarle con popup (se non precedentemente disattivate)
// Se Utente accetta il mio popup viene chiamato: initialize() che fa requestPermission() 
// (che quindi richiede i permessi veri e propri)  e se l'utente da il permesso: → refreshToken()
// il refreshtoken chiede il token a fcm e lo salva a DB
// Il Service Worker gestisce tutto il resto (visualizzazione notifiche, click.)

let app, messaging;
let firebaseInitialized = false;

// Configurazione Firebase hardcoded
const firebaseConfig = {
  "apiKey": "AIzaSyDTJtKaWSjoZJQ1hD8zPD4kmF3NHkLWpLY",
  "authDomain": "inufficio-pwa.firebaseapp.com",
  "projectId": "inufficio-pwa",
  "storageBucket": "inufficio-pwa.firebasestorage.app",
  "messagingSenderId": "537128060963",
  "appId": "1:537128060963:web:854d6ca68fe204379c4996"
};

// Inizializza Firebase
async function initializeFirebase() {
  if (firebaseInitialized) return { app, messaging };

  try {
    app = initializeApp(firebaseConfig);
    messaging = getMessaging(app);
    firebaseInitialized = true;
    
    console.log('FCM: Firebase inizializzato correttamente nell\'app principale');
    return { app, messaging };
    
  } catch (error) {
    console.error('FCM: Errore inizializzazione Firebase:', error);
    throw error;
  }
}

class FcmManager {
  constructor(vapidKey, promptFn = null) {
    this.vapidKey = vapidKey || process.env.REACT_APP_VAPID_KEY;
    this.promptFn = promptFn;
    this.currentToken = null;
    this.isInitialized = false;
  }

  setPromptFunction(fn) {
    this.promptFn = fn;
  }

  // Attende che il Service Worker sia pronto
  async waitForServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker non supportato');
    }

    try {
      // Attende che il SW sia registrato e attivo
      const registration = await navigator.serviceWorker.ready;
      console.log('FCM: Service Worker pronto:', registration);
      return registration;
    } catch (error) {
      console.error('FCM: Errore attesa Service Worker:', error);
      throw error;
    }
  }

  // Inizializza FCM (da chiamare dopo il login)
  async initialize() {
    if (this.isInitialized) {
      console.log('FCM: Già inizializzato');
      return true;
    }
    
    try {
      console.log('FCM: Inizializzazione in corso...');
      
      // Assicurati che Firebase sia inizializzato
      await initializeFirebase();
      
      // Richiedi permessi notifiche se non già concessi
      const permission = await this.requestPermission();
      console.log('FCM: Permesso ottenuto:', permission);
      
      if (permission === 'granted') {
        const token = await this.refreshToken();
        if (token) {
          this.setupForegroundListener();
          this.isInitialized = true;
          console.log('FCM: Inizializzazione completata con successo');
          return true;
        }
      }
      
      console.log('FCM: Inizializzazione fallita');
      return false;
    } catch (error) {
      console.error('FCM: Errore inizializzazione:', error);
      return false;
    }
  }

  // Richiede permessi per le notifiche
  async requestPermission() {
    try {
      if (Notification.permission === 'granted') {
        console.log('FCM: Permessi già concessi');
        return 'granted';
      }

      console.log('FCM: Richiedendo permessi notifiche...');
      const permission = await Notification.requestPermission();
      console.log('FCM: Permesso notifiche:', permission);
      return permission;
    } catch (error) {
      console.error('FCM: Errore richiesta permessi:', error);
      return 'denied';
    }
  }

  // Ottiene o aggiorna il token FCM
  async refreshToken(notSendToServer = false) {
    try {
      if (!('serviceWorker' in navigator)) {
        console.warn('FCM: Service Worker non supportato');
        return null;
      }

      console.log('FCM: Ottenendo token...');
      
      // Assicurati che Firebase sia inizializzato
      await initializeFirebase();

      // Attende che il Service Worker sia pronto
      const registration = await this.waitForServiceWorker();

      const token = await getToken(messaging, {
        vapidKey: this.vapidKey,
        serviceWorkerRegistration: registration
      });

      if (token) {
        this.currentToken = token;
        localStorage.setItem('fcm_token', token);
        console.log('FCM: Token ottenuto e salvato');
        
        if (!notSendToServer) {
          // Invia il token al server
          await this.sendTokenToServer(token);
        }
        return token;
      } else {
        console.log('FCM: Nessun token disponibile');
        return null;
      }
    } catch (error) {
      console.error('FCM: Errore nel recuperare token:', error);
      return null;
    }
  }

  // Ottiene il token corrente (senza richiedere permessi)
  getCurrentToken() {
    const token = this.currentToken || localStorage.getItem('fcm_token');
    console.log('FCM: Token corrente:', token ? 'Presente' : 'Non presente');
    return token;
  }

  // Controlla se le notifiche sono disponibili e attive
  isNotificationAvailable() {
    const available = 'Notification' in window && 
                     'serviceWorker' in navigator && 
                     Notification.permission === 'granted';
    console.log('FCM: Notifiche disponibili:', available);
    return available;
  }

  // Controlla solo se le notifiche sono supportate (senza permessi)
  isNotificationSupported() {
    const supported = 'Notification' in window && 'serviceWorker' in navigator;
    console.log('FCM: Notifiche supportate:', supported);
    return supported;
  }

  // Setup listener per messaggi in foreground (quando l'app è aperta)
  setupForegroundListener() {
    if (!messaging) {
      console.log('FCM: Messaging non disponibile per listener foreground');
      return;
    }
    
    console.log('FCM: Configurando listener per messaggi in foreground');
    
    onMessage(messaging, (payload) => {
      console.log('FCM: Messaggio ricevuto in foreground:', payload);
      
      // Mostra notifica personalizzata se l'app è in foreground
      if (payload.notification) {
        this.showForegroundNotification(payload.notification, payload.data);
      }
    });
  }

  // Mostra notifica quando l'app è in foreground
  showForegroundNotification(notification, data = {}) {
    if (!this.isNotificationAvailable()) {
      console.log('FCM: Non posso mostrare notifica foreground - permessi mancanti');
      return;
    }

    const { title, body, icon } = notification;
    
    console.log('FCM: Mostrando notifica foreground:', title);
    
    const notif = new Notification(title || 'Nuova notifica', {
      body: body || '',
      icon: icon || '/favicon.ico',
      badge: '/favicon.ico',
      data: data,
      requireInteraction: false,
      tag: 'doblone-foreground'
    });

    // Gestisci click sulla notifica in foreground
    notif.onclick = function(event) {
      console.log('FCM: Notifica foreground cliccata');
      event.preventDefault();
      window.focus();
      notif.close();
    };

    // Auto-chiudi dopo 5 secondi
    setTimeout(() => {
      notif.close();
    }, 5000);
  }

  // Invia token al server
  async sendTokenToServer(token) {
    try {
      console.log('FCM: Inviando token al server...');
      const result = await updateFcmToken(token);
      console.log('FCM: Token inviato al server con successo');
      return result;
    } catch (error) {
      console.error('FCM: Errore invio token al server:', error);
      throw error;
    }
  }

  // Mostra popup per attivare notifiche (da chiamare post-login)
  async showNotificationPrompt() {
    console.log('FCM: Mostrando prompt notifiche');
    
    if (this.isNotificationAvailable()) {
      console.log('FCM: Notifiche già attive');
      return true;
    }

    if (!this.isNotificationSupported()) {
      console.log('FCM: Notifiche non supportate');
      toast.warning('Le notifiche push non sono supportate su questo dispositivo');
      return false;
    }
  
    const userWantsNotifications = await this.promptFn({
        title: 'Vuoi ricevere notifiche push?',
        message: 'Servono per rimanere aggiornato su chiamate, appuntamenti e altre comunizazioni importanti dalla segreteria.'
    });

    if (userWantsNotifications) {
      console.log('FCM: Utente ha accettato le notifiche');
      const success = await this.initialize();
      if (success) {
        toast.success('Notifiche attivate con successo!');
        return true;
      } else {
        toast.error('Impossibile attivare le notifiche. Controlla le impostazioni del browser.');
        return false;
      }
    } else {
      console.log('FCM: Utente ha rifiutato le notifiche');
    }

    return false;
  }

  // Disattiva notifiche
  disable() {
    console.log('FCM: Disattivando notifiche');
    this.currentToken = null;
    localStorage.removeItem('fcm_token');
    this.isInitialized = false;
  }

  // Metodo di debug per controllare lo stato
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      hasToken: !!this.getCurrentToken(),
      notificationPermission: Notification.permission,
      isSupported: this.isNotificationSupported(),
      isAvailable: this.isNotificationAvailable()
    };
  }
}

// Esporta istanza singleton
const fcmManager = new FcmManager();
export { fcmManager };